import { MoreOutlined, StarFilled, StarOutlined } from "@ant-design/icons"
import { Button, Dropdown, Flex, Table, type TableProps, Typography } from "antd"
import type { MenuItemType } from "antd/es/menu/interface"
import React from "react"
import { useTranslation } from "react-i18next"

import { getFormatDate } from "../../../utils/date/get-date-safe.ts"
import { getFileIcon } from "../../Project/ProjectViewPage/helpers/file.tsx"
import { type FileItem } from "../types/file-item.types.ts"

const { Text } = Typography

interface FilesListViewProps {
  items: FileItem[]
  actions: (file: FileItem) => MenuItemType[]
  selectedKeys: Set<string>
  onToggleSelect: (file: FileItem) => void
  onPreview: (file: FileItem) => void
  isFavorite?: (file: FileItem) => boolean
  onToggleFavorite?: (file: FileItem) => void
}

const EMPTY_CELL = "—"

export function FilesListView({
  items,
  actions,
  selectedKeys,
  onToggleSelect,
  onPreview,
  isFavorite,
  onToggleFavorite
}: FilesListViewProps) {
  const { t } = useTranslation()

  const columns: TableProps<FileItem>["columns"] = [
    {
      title: t("files.columns.name"),
      key: "name",
      align: "left",
      render: (_, file) => (
        <Flex align="center" gap="small">
          {onToggleFavorite && (
            <Button
              type="text"
              size="small"
              aria-label={t("files.actions.favorite", { name: file.name })}
              icon={
                isFavorite?.(file) ? (
                  <StarFilled className="text-warning" />
                ) : (
                  <StarOutlined />
                )
              }
              onClick={() => onToggleFavorite(file)}
            />
          )}
          <span className="[&_svg]:text-base">{getFileIcon(file.mimeType)}</span>
          <a onClick={() => onPreview(file)}>
            <Text ellipsis={{ tooltip: file.name }}>{file.name}</Text>
          </a>
        </Flex>
      )
    },
    {
      title: t("files.columns.type"),
      key: "type",
      render: (_, file) =>
        file.fileTypeLabel ??
        (file.source === "album-attachment"
          ? t("files.columns.typeFile")
          : t("files.columns.typeDocument"))
    },
    {
      title: t("files.columns.size"),
      key: "size",
      render: (_, file) => file.formattedFileSize || EMPTY_CELL
    },
    {
      title: t("files.columns.documentType"),
      key: "documentType",
      responsive: ["lg"],
      render: (_, file) => file.documentTypeLabel || EMPTY_CELL
    },
    {
      title: t("files.columns.version"),
      key: "version",
      responsive: ["lg"],
      render: (_, file) =>
        file.version !== undefined
          ? t("fileViewer.detailsPanel.versionShort", { version: file.version })
          : EMPTY_CELL
    },
    {
      title: t("files.columns.modified"),
      key: "modified",
      render: (_, file) => getFormatDate(file.updated) ?? EMPTY_CELL
    },
    {
      title: t("files.columns.actions"),
      key: "actions",
      width: 80,
      render: (_, file) => (
        <Dropdown trigger={["click"]} menu={{ items: actions(file) }}>
          <Button
            type="text"
            size="small"
            icon={<MoreOutlined />}
            aria-label={t("files.actions.menu", { name: file.name })}
          />
        </Dropdown>
      )
    }
  ]

  return (
    <Table<FileItem>
      rowKey="key"
      size="small"
      columns={columns}
      dataSource={items}
      pagination={false}
      scroll={{ x: "max-content" }}
      rowSelection={{
        selectedRowKeys: [...selectedKeys],
        onSelect: (file) => onToggleSelect(file)
      }}
    />
  )
}
